import { HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE } from 'app/modules/dataSettings/constants';
import {
  Unit21DataClassifier,
  DataSettingFieldType,
  NativeDataSettingsConfigResponse,
  CustomDataSettingsConfigResponse,
  OrgDataSettingsConfig,
} from 'app/modules/dataSettings/responses';
import { keyPathToLabel } from 'app/modules/dataSettings/utils';
import {
  BUSINESS_DATE_FIELD_DESCRIPTION,
  BUSINESS_DATE_FIELD_VALUE,
  CUSTOM_DATETIME_FIELDS,
  MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
  EventSubTypes,
  EXPERIMENTAL_OPERATORS,
} from 'app/modules/detectionModels/constants';
import {
  transformNativeDataSettingsValueToFieldValue,
  transformCustomDataSettingsValueToFieldValue,
} from 'app/modules/detectionModels/helpers';
import { DmEventByObjectFactFormValues } from 'app/modules/rulesAdvanced/components/Facts/models';
import {
  DmFact,
  DurationValue,
  DmEventSubtype,
  DmAggregateFunctions,
  DmObjectToFlag,
  CompositeQueryTreeNode,
  QueryTreeNode,
  DmEventByObjectFact,
  DmFactTypes,
  RtrEventByObjectFact,
  FeDmFactTypes,
  DmEventByObjectFactType,
  FeDmEventByObjectFactType,
  MultiplePerspectivesAggregation,
  DmEntityValueFact,
  CompositeTypes,
  ValueTypes,
  IRSchema,
} from 'app/modules/rulesAdvanced/models';
import { U21SelectOptionProps } from 'app/shared/u21-ui/components';
import { truncate } from 'lodash';

const AGGREGATE_TXN_OPTION_DESCRIPTION = {
  amount: 'amounts',
  sent_amount: 'sent amounts',
  received_amount: 'received amounts',
  id: 'transactions',
  internal_fee: 'internal fees',
  external_fee: 'external fees',
  receiver_entity_id: 'receiver entities',
  sender_entity_id: 'sender entities',
  internal_txn_type: 'internal transactions',
  status: 'status',
  sent_currency: 'sent currencies',
  sender_instrument_id: 'sender instruments',
  received_currency: 'received currencies',
  receiver_instrument_id: 'receiver instruments',
};

const AGGREGATE_ACTION_OPTION_DESCRIPTION = {
  id: 'actions',
  entity_id: 'entities',
  txn_instrument_id: 'instruments',
  device_id: 'devices',
  status: 'statuses',
};

const ACCEPTABLE_CLASSIFIERS: readonly (keyof typeof Unit21DataClassifier)[] = [
  'ACTION_EVENT',
  'EVENT',
  'INSTRUMENT',
  'ENTITY',
];

const SUBTYPE_THAT_NEEDS_OPTION_FILTERS: readonly DmEventSubtype[] = [
  'TXN_EVENT',
  'ACTION_EVENT',
  'FIELD',
];

const NO_OPTIONS_OPTION = {
  key: 0,
  value: {
    field: 'No options',
    datatype: 'text',
    type: ValueTypes.FIELD,
    model: 'action_event',
  },
  text: 'No options',
};

export const AGGREGATE_FUNCTION_DESCRIPTION: {
  [k in DmAggregateFunctions]: string;
} = {
  COUNT: 'count',
  SUM: 'sum up',
  AVERAGE: 'average out',
  MAXIMUM: 'maximum',
  MINIMUM: 'minimum',
};

// HARD CODED FOR NOW - NEED TO SOMEHOW GET THESE FROM CDS
const DEFAULT_COUNT_FIELDS_FN = (model: string) => [
  {
    key: 1,
    text: model === 'action_event' ? 'Action / ID' : 'Transaction / ID',
    value: {
      field: 'id',
      datatype: 'text',
      type: ValueTypes.FIELD,
      model,
    },
  },
];

const MATH_AGGREGATE_FIELDS: readonly string[] = [
  'entity_id',
  'instrument_id',
  'sender_entity_id',
  'sender_instrument_id',
  'receiver_entity_id',
  'receiver_instrument_id',
  'amount',
  'sent_amount',
  'received_amount',
  'fee', // NOTE: previously "internal_fee" (still what the column is called in the DB). "fee" is what DIM has labeled this native data field
  'external_fee',
];

const COUNTABLE_AGGREGATE_FIELDS: readonly string[] = [
  'entity_id',
  'instrument_id',
  'sender_entity_id',
  'sender_instrument_id',
  'receiver_entity_id',
  'receiver_instrument_id',
  'amount',
  'status',
  'sent_currency',
  'received_currency',
  'ip_address',
];

// we currently don't support custom data settings with the following primitive types
const UNSUPPORTED_CUSTOM_DATA_DATA_TYPES: Set<DataSettingFieldType> = new Set([
  DataSettingFieldType.LIST,
  DataSettingFieldType.ENTITY_REFERENCE,
  DataSettingFieldType.INSTRUMENT_REFERENCE,
  DataSettingFieldType.JSON,
]);

const isTypeEqual = (
  nativeDataType: Unit21DataClassifier,
  factSubtype: DmEventSubtype,
) => {
  if (factSubtype === 'TXN_EVENT') return nativeDataType === 'EVENT';

  return nativeDataType === factSubtype;
};

export const generateAggregateFieldOptionsForRealTimeRules = (
  nativeData: OrgDataSettingsConfig[],
  customData: OrgDataSettingsConfig[],
  eventSubtype: DmEventSubtype,
  aggregateFunction: DmAggregateFunctions,
) => {
  const defaultOptions = [{ text: 'default', value: 'value' }];
  if (SUBTYPE_THAT_NEEDS_OPTION_FILTERS.includes(eventSubtype)) {
    let options: U21SelectOptionProps[] = [];

    options = options.concat(
      nativeData
        .filter(
          (i: NativeDataSettingsConfigResponse) =>
            i.native_key !== 'address' &&
            i.data_type !== DataSettingFieldType.LIST &&
            (aggregateFunction === DmAggregateFunctions.COUNT ||
              i.data_type === DataSettingFieldType.NUMBER) &&
            isTypeEqual(i.unit21_data_classifier, eventSubtype),
        )
        .map((i: NativeDataSettingsConfigResponse) => {
          const { description } = i;
          const truncatedDescription = truncate(description, {
            length: MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
          });
          return {
            key: i.id,
            value: transformNativeDataSettingsValueToFieldValue(i),
            text: formatAggregateFieldString(
              HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE[
                i.unit21_data_classifier
              ] || 'Unknown model',
              keyPathToLabel(i, false, ' / '),
            ),
            description: truncatedDescription,
            tooltip:
              description && description.length > 128 ? description : undefined,
          };
        }),
    );
    // This is appending custom data from the new custom_data_settings work done by our DIM team
    options = options.concat(
      customData
        .filter(
          (i: CustomDataSettingsConfigResponse) =>
            ACCEPTABLE_CLASSIFIERS.includes(i.unit21_data_classifier) &&
            !UNSUPPORTED_CUSTOM_DATA_DATA_TYPES.has(i.data_type) &&
            (aggregateFunction === DmAggregateFunctions.COUNT ||
              i.data_type === DataSettingFieldType.NUMBER) &&
            isTypeEqual(i.unit21_data_classifier, eventSubtype) &&
            (eventSubtype === 'ACTION_EVENT' ? i.data_type === 'NUMBER' : true),
        )
        .map((i: CustomDataSettingsConfigResponse) => {
          const { description } = i;
          const truncatedDescription = truncate(description, {
            length: MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
          });
          return {
            key: i.id,
            value: transformCustomDataSettingsValueToFieldValue(i),
            text: formatAggregateFieldString(
              `${
                HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE[
                  i.unit21_data_classifier
                ] || 'Unknown model'
              } / Custom data`,
              keyPathToLabel(i, false, ' / '),
            ),
            description: truncatedDescription,
            tooltip:
              description &&
              description.length > MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH
                ? description
                : undefined,
          };
        }),
    );

    return options.length === 0 ? [NO_OPTIONS_OPTION] : options;
  }

  return defaultOptions;
};

export const generateAggregateFieldOptionsForDynamicRules = (
  nativeData: NativeDataSettingsConfigResponse[],
  customData: CustomDataSettingsConfigResponse[],
  eventSubtype: DmEventSubtype,
  aggregateFunction: DmAggregateFunctions,
) => {
  let aggregatableFields: readonly string[];

  if (aggregateFunction === DmAggregateFunctions.COUNT) {
    aggregatableFields = COUNTABLE_AGGREGATE_FIELDS;
  } else if (eventSubtype === 'TXN_EVENT') {
    aggregatableFields = MATH_AGGREGATE_FIELDS;
  } else {
    aggregatableFields = [];
  }

  const defaultOptions = [{ text: 'default', value: 'value' }];
  if (SUBTYPE_THAT_NEEDS_OPTION_FILTERS.includes(eventSubtype)) {
    // Generate default count field options first
    const aggModel =
      eventSubtype === 'ACTION_EVENT' ? 'action_event' : 'txn_event';
    let options: U21SelectOptionProps[] = [];

    // If it's a COUNT operation, add the default ID field first
    if (aggregateFunction === DmAggregateFunctions.COUNT) {
      options = DEFAULT_COUNT_FIELDS_FN(aggModel);
    }

    options = options.concat(
      nativeData
        .filter(
          (i: NativeDataSettingsConfigResponse) =>
            i.data_type !== DataSettingFieldType.LIST &&
            aggregatableFields.includes(i.native_key) &&
            isTypeEqual(i.unit21_data_classifier, eventSubtype),
        )
        // this will make the order of the native keys match that of aggregatableFields
        .toSorted(
          (a, b) =>
            aggregatableFields.indexOf(a.native_key) -
            aggregatableFields.indexOf(b.native_key),
        )
        .map((i: NativeDataSettingsConfigResponse) => {
          const { description } = i;
          const truncatedDescription = truncate(description, {
            length: MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
          });
          return {
            key: i.id,
            value: transformNativeDataSettingsValueToFieldValue(i),
            text: formatAggregateFieldString(
              HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE[
                i.unit21_data_classifier
              ] || 'Unknown Model',
              keyPathToLabel(i, false, ' / '),
            ),
            description: truncatedDescription,
            tooltip:
              description && description.length > 128 ? description : undefined,
          };
        }),
    );
    // This is appending custom data from the new custom_data_settings work done by our DIM team
    options = options.concat(
      customData
        .filter(
          (i: CustomDataSettingsConfigResponse) =>
            ACCEPTABLE_CLASSIFIERS.includes(i.unit21_data_classifier) &&
            !UNSUPPORTED_CUSTOM_DATA_DATA_TYPES.has(i.data_type) &&
            isTypeEqual(i.unit21_data_classifier, eventSubtype) &&
            (eventSubtype === 'ACTION_EVENT'
              ? i.data_type === 'NUMBER' || i.data_type === 'TEXT'
              : true),
        )
        .map((i: CustomDataSettingsConfigResponse) => {
          const { description } = i;
          const truncatedDescription = truncate(description, {
            length: MAX_DROPDOWN_OPTION_DESCRIPTION_LENGTH,
          });
          return {
            key: i.id,
            value: transformCustomDataSettingsValueToFieldValue(i),
            text: formatAggregateFieldString(
              `${
                HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE[
                  i.unit21_data_classifier
                ] || 'Unknown Model'
              } / Custom Data`,
              keyPathToLabel(i, false, ' / '),
            ),
            description: truncatedDescription,
            tooltip:
              description && description.length > 128 ? description : undefined,
          };
        }),
    );

    return options.length === 0 ? [NO_OPTIONS_OPTION] : options;
  }

  return defaultOptions;
};

export const generateCustomTxnDatetimeFieldOptions = (
  nativeData: NativeDataSettingsConfigResponse[],
  eventSubtype: DmEventSubtype,
): U21SelectOptionProps[] => {
  const options = nativeData
    .filter(
      (i) =>
        CUSTOM_DATETIME_FIELDS.includes(i.native_key) &&
        isTypeEqual(i.unit21_data_classifier, eventSubtype),
    )
    .map((i) => ({
      key: i.id,
      value: transformNativeDataSettingsValueToFieldValue(i),
      text: formatAggregateFieldString(
        HUMAN_READABLE_CUSTOM_DATA_OBJECT_TYPE[i.unit21_data_classifier] ||
          'Unknown Model',
        keyPathToLabel(i, false, ' / '),
      ),
      description:
        i.native_key === BUSINESS_DATE_FIELD_VALUE.field
          ? BUSINESS_DATE_FIELD_DESCRIPTION
          : undefined,
    }));

  // Hardcode business date until we have it as a native data setting
  if (
    eventSubtype === 'TXN_EVENT' &&
    !nativeData.some((i) => i.native_key === BUSINESS_DATE_FIELD_VALUE.field)
  ) {
    options.push({
      key: -1,
      value: BUSINESS_DATE_FIELD_VALUE,
      text: 'Transaction / Business date',
      description: BUSINESS_DATE_FIELD_DESCRIPTION,
    });
  }

  return options;
};

export const isEventByObjectFact = (
  fact: DmFact,
): fact is DmEventByObjectFact => {
  return fact.type === DmFactTypes.EVENT_BY_OBJECT_FACT;
};

export const isMultiplePerspectivesAggregation = (
  fact: DmFact,
): fact is MultiplePerspectivesAggregation => {
  return fact.type === DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION;
};

export const isRtrEventByObjectFact = (
  fact: DmFact,
): fact is RtrEventByObjectFact => {
  return (
    fact.type === DmFactTypes.EVENT_BY_OBJECT_FACT &&
    Object.hasOwn(fact, 'include_current_evaluated_event')
  );
};

export const isEntityValueFact = (fact: DmFact): fact is DmEntityValueFact => {
  return fact.type === DmFactTypes.ENTITY_VALUE_FACT;
};

export const validateFactName = (factName: string | undefined) => {
  if (!factName || factName.length < 1) {
    return 'Variable Name is required.';
  } else if (factName.match(/^\d/g)) {
    return 'Variable Name should not begin with a number.';
  } else if (factName.match(/[^a-zA-Z0-9_\s]/g)) {
    return 'Variable Name must be alphanumeric, _, or white space.';
  }
  return '';
};

export const validateComposite = (
  composite: CompositeQueryTreeNode,
): boolean => {
  if (!composite || !Object.prototype.hasOwnProperty.call(composite, 'type'))
    return false;

  return composite.operands.every((operand: QueryTreeNode): boolean => {
    if (
      operand &&
      (operand.type === CompositeTypes.RULE_CONDITION_COMPOSITE ||
        operand.type === CompositeTypes.COMPOSITE)
    )
      return validateComposite(operand);
    return true;
  });
};

export function instanceOfCompositeQueryTreeNode(
  composite: QueryTreeNode,
): composite is CompositeQueryTreeNode {
  return composite !== null && 'meta_ui' in composite;
}

export const getFeDmEventByObjectFactType = (
  factType: DmEventByObjectFactType,
  eventSubType: DmEventSubtype,
): FeDmEventByObjectFactType => {
  if (
    factType === DmFactTypes.EVENT_BY_OBJECT_FACT ||
    factType === DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION
  ) {
    return eventSubType === EventSubTypes.TXN_EVENT
      ? FeDmFactTypes.EVENT_BY_OBJECT_TRANSACTION_FACT
      : FeDmFactTypes.EVENT_BY_OBJECT_ACTION_FACT;
  }

  return factType;
};

// TODO: chore/sc-104491/delete-legacy-code
export const getCustomFactType = (
  fact: DmFact | DmEventByObjectFactFormValues | undefined,
) => {
  if (!fact) {
    return '';
  }

  if (
    fact.type === DmFactTypes.EVENT_BY_OBJECT_FACT ||
    fact.type === DmFactTypes.MULTIPLE_PERSPECTIVES_AGGREGATION
  ) {
    switch (fact.event_subtype) {
      case 'TXN_EVENT': {
        return 'EVENT_BY_OBJECT_TRANSACTION_FACT';
      }
      case 'ACTION_EVENT': {
        return 'EVENT_BY_OBJECT_ACTION_FACT';
      }
      default: {
        return '';
      }
    }
  } else {
    return fact.type;
  }
};

export const getTimeString = (timeObj: DurationValue | undefined) => {
  if (!timeObj) {
    return '';
  }

  return timeObj.value === 0
    ? 'now'
    : `${timeObj.value} ${timeObj.unit.toLowerCase()}${
        timeObj.value > 1 ? 's' : ''
      } ago`;
};

export const getNameString = (id: string | undefined) => {
  if (!id) {
    return 'Variable';
  }

  return id === '' ? 'Variable' : id;
};

export const getTypeString = (
  type: DmEventSubtype | undefined,
  field: string,
  model: string,
) => {
  if (!type || field === '' || model === '') {
    return '';
  }

  switch (type) {
    case 'ACTION_EVENT':
      return `action ${getModelType(model)} data`;
    case 'TXN_EVENT':
      return `transactional ${getModelType(model)} data`;
    case 'FIELD':
      return `all aggregate fields' ${getFieldString(type, field, model)} data`;
    case 'CONSORTIUM':
      return `consortium ${getModelType(model)} data`;
    default:
      return '';
  }
};

export const getModelType = (model: string): string => {
  if (!model || model === '') {
    return '';
  }

  const modelArr = model.split('_');

  return modelArr[modelArr.length - 1].toLocaleLowerCase();
};

export const getModelString = (model: string): string => {
  if (!model || model === '') {
    return '';
  }

  const modelArr = model.toLocaleLowerCase().split('_');

  if (modelArr.length === 1) {
    return `${modelArr[0]}`;
  }

  return modelArr[0] === 'txn'
    ? `transaction ${modelArr[1]}`
    : `${modelArr[0]} ${modelArr[1]}`;
};

export const getFieldString = (
  type: DmEventSubtype | undefined,
  field: string,
  model: string,
) => {
  if (!type || field === '' || model === '') {
    return '';
  }

  const fieldArr = field.split('.');

  if (type === 'ACTION_EVENT') {
    return Object.prototype.hasOwnProperty.call(
      AGGREGATE_ACTION_OPTION_DESCRIPTION,
      field,
    )
      ? AGGREGATE_ACTION_OPTION_DESCRIPTION[field]
      : `${getModelString(model)}'s ${fieldArr[fieldArr.length - 1]
          .replace(/_/g, ' ')
          .replace(/\./g, ' / ')}`;
  } else if (type === 'TXN_EVENT') {
    return Object.prototype.hasOwnProperty.call(
      AGGREGATE_TXN_OPTION_DESCRIPTION,
      field,
    )
      ? AGGREGATE_TXN_OPTION_DESCRIPTION[field]
      : `${getModelString(model)}'s ${fieldArr[fieldArr.length - 1]
          .replace(/_/g, ' ')
          .replace(/\./g, ' / ')}`;
  } else if (type === 'CONSORTIUM') {
    return `${getModelString(model)}'s ${fieldArr[fieldArr.length - 1]
      .replace(/_/g, ' ')
      .replace(/\./g, ' / ')}`;
  }

  return `${getModelString(model)} ${fieldArr[fieldArr.length - 1]
    .replace(/_/g, ' ')
    .replace(/\./g, ' / ')}`;
};

export const filterAggregateOptions = (
  type: DmEventSubtype,
  model: string,
  aggregateFunction: DmAggregateFunctions,
  field: string,
  datatype?: string,
  dmObject?: DmObjectToFlag,
): boolean => {
  const fieldArr = field.split('.');

  if (fieldArr[0] === 'custom_data') {
    return model === 'event' || model === dmObject?.toLocaleLowerCase();
  }

  if (type === 'TXN_EVENT') {
    return (
      model === 'txn_event' &&
      (aggregateFunction === DmAggregateFunctions.COUNT
        ? field === 'amount' || datatype !== 'number'
        : datatype === 'number')
    );
  } else if (type === 'ACTION_EVENT') {
    return model === 'action_event';
  }

  // Condition for Field Options
  return model !== 'txn_event' && model !== 'action_event';
};

export const formatAggregateFieldString = (
  model: string,
  field: string,
): string => {
  if (model === '') {
    return '';
  }

  return `${model.replace(/_/g, ' ')} / ${field
    .replace(/_/g, ' ')
    .replace(/\./g, ' / ')}`;
};

export const getBusinessDateString = (durationValue: DurationValue) => {
  if (durationValue.value === 0) {
    return "today's business day";
  } else if (durationValue.value === 1) {
    return 'the previous business day';
  }
  return `${durationValue.value} business days ago`;
};

function isCompositeShaped(node: unknown): node is CompositeQueryTreeNode {
  return (
    node !== null &&
    typeof node === 'object' &&
    'operator' in node &&
    'operands' in node &&
    Array.isArray(node.operands)
  );
}

export const compositeContainsOperator = (
  composite: CompositeQueryTreeNode,
  operator: string,
): boolean => {
  if (!composite?.operands) return false;
  if (composite.operator === operator) return true;
  return composite.operands.some((operand: QueryTreeNode): boolean => {
    if (operand && isCompositeShaped(operand))
      return compositeContainsOperator(operand, operator);
    return false;
  });
};

export const stringContainsExperimentalOperator = (text: string): boolean =>
  EXPERIMENTAL_OPERATORS.some((op) => text.toUpperCase().includes(op));

export const treeContainsExperimentalOperator = (
  node: QueryTreeNode,
): boolean =>
  isCompositeShaped(node) &&
  EXPERIMENTAL_OPERATORS.some((op) => compositeContainsOperator(node, op));

export const irSchemaContainsExperimentalOperators = (
  irSchema: IRSchema | null,
): boolean => {
  if (!irSchema) return false;

  return [
    irSchema.rule_condition,
    ...irSchema.facts.flatMap((fact) => Object.values(fact)),
  ]
    .filter(isCompositeShaped)
    .some(treeContainsExperimentalOperator);
};
